import React from 'react';
import '../../../styles.scss';

import {
  OwcTypography, OwcModalDialog, OwcButton, OwcExpandable,
  OwcInput, OwcTable, OwcTableHeader, OwcTableHeaderCell, OwcTableBody,
} from '@one/react';
import { configData } from "../../../config.js";
import { fetchSigned } from "../../../shared/Utilities.js"
import CountrySelector from "../../general/CountrySelector.js";
import AgreementSelection from '../../useCase/AgreementSelection.js';
import ReferenceListSelector from '../../general/ReferenceListSelector.js'
import DelayedTooltip from "../../../components/general/DelayedTooltip.js";


/**
 * @copyright Roche 2025
 * @author Nick Draper
 */

/**
 * Specific control for querying and displaying the Country report
 */
class CountryReportSpecification extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      deviceDetailFilter: "",
      filterDeviceDetailsBy: "Serial No",
      countryList: [],
      systemTypeList: [],
      selectedSystemTypes: [],
      specificationExpanded: true,
      agreementExpanded: true,
      countrySelectorVisible: false,
      deviceDetailList: [
        "Serial No",
        "Account No",
        "Lab ID",
        "Device location",
      ],
      agreementList: [],
      resetAgreementControl: Math.random(),
    }
  }

  REPORT_DISCLAIMER = "This report assumes computational governance is applied correctly. Where computational governance is not applied, additional devices may be sending data to the use case, which can not be determined exactly and hence is not reported (as the skipped rules may include any combination of entitlements, system types, customers, etc.)"

  /**
   * Runs once after construction after everything is initialised
   */
  componentDidMount() {
    // load the reference system type data
    fetchSigned(configData.REFDATA_API_URL + "?includeInactive=true")
      .then(res => res.json())
      .then(
        (result) => {
          const filteredSystemTypeList = result.filter(value =>
            value.type === "Reference System Type"
          );

          this.setState({
              systemTypeList: filteredSystemTypeList
            }
          );
        },
        // Note: it's important to handle errors here
        // instead of a catch() block so that we don't swallow
        // exceptions from actual bugs in components.
        (error) => {this.setState({error})}
      );
  }

  generateURLParameters() {
    const parameters = [];
    const selectedCountries = this.state.countryList.join(",");
    if (selectedCountries && selectedCountries.trim() !== "") {
      parameters.push(`country=${selectedCountries}`);
    }

    if (this.state.deviceDetailFilter.trim() !== "") {
      let deviceFilterType = "";
      switch (this.state.filterDeviceDetailsBy) {
        case "Lab ID":
          deviceFilterType = "labId";
          break;
        case "Serial No":
          deviceFilterType = "serialNo";
          break;
        case "Account No":
          deviceFilterType = "accountNumber";
          break;
        case "Device location":
          deviceFilterType = "instrumentLocation";
          break;
        default:
          deviceFilterType = "";
      }
      if (deviceFilterType) {
        parameters.push(`${deviceFilterType}=${this.state.deviceDetailFilter}`);
      }
    }

    const selectedAgreements = this.state.agreementList.map(agreement => agreement.customerAgreementId).join(",");
    if (selectedAgreements && selectedAgreements.trim() !== "") {  
      parameters.push(`customerAgreementId=${selectedAgreements}`);
    }

    const selectedSystemTypes = this.state.selectedSystemTypes.join(",");
    if (selectedSystemTypes && selectedSystemTypes.trim() !== "") {
      parameters.push(`referenceSystemType=${selectedSystemTypes}`);
    }
    
    if (parameters.length > 0) {
      return `?${parameters.join("&")}`;
    }
    return "";
  }

  handleSubmit = () => {
    this.setState({
      specificationExpanded: false,
    });
    const parameters = this.generateURLParameters();
    this.props.onSubmit(parameters);
  }

  handleCancel = () => {
    this.props.onCancel();
  }

  handleFormClear = () => {
    this.setState({
      deviceDetailFilter: "",
      countryList: [],
      selectedSystemTypes: [],
      agreementList: [],
      resetAgreementControl: Math.random(),
    });
  }

  handleCountrySelectionChange(selectedCountryCodes) {
    this.setState({
      countryList: selectedCountryCodes,
    });
  }

  renderCountrySelectionDialog() {
    const modalStyle = { width: '100%' };
    return (
      <OwcModalDialog disableBackdropClick disableEscapeKeydown 
          className='modal-fit-content'
          disableScrollLock="false" hideBackdrop="false" size="large"
          cardProps={{ style: modalStyle, className: 'modal-fixed-width' }}
          visible={this.state.countrySelectorVisible}>
        <div style={{ justifyContent: 'flex-end', padding: '10px' }}>
          <CountrySelector 
            title="Countries"
            selectedCountryCodes={this.state.countryList}
            onSelectedChange={(selectedCountryCodes) => this.handleCountrySelectionChange(selectedCountryCodes)} />
        </div>
        <div slot="header" style={{ justifyContent: 'flex-end', padding: '10px' }}>
          <OwcTypography variant="title6">
            Select the desired countries
          </OwcTypography>
        </div>
        <div slot="footer" style={{ justifyContent: 'flex-end', padding: '5px' }}>
          <table width="100%">
            <tbody><tr>
              <td align="left">
                <OwcButton style={{ width: "fit-content" }} onclick={() => this.setState({ countrySelectorVisible: false })}>
                  Close
                </OwcButton>
              </td>
            </tr></tbody>
          </table>
        </div>
      </OwcModalDialog>
    );
  }

  renderCountryFilter() {
    let description = null;
    if (Object.keys(this.props.label).length > 0) {
      const dataProductAttributes = this.props.label[configData.LABEL_KEYS.DATA_PRODUCT_ATTRIBUTES_KEY][configData.LABEL_KEYS.CHILDREN_KEY];
      if (dataProductAttributes !== undefined) {
        if (dataProductAttributes[configData.LABEL_KEYS.DESCRIPTION_KEY] !== undefined) {
          description = dataProductAttributes[configData.LABEL_KEYS.DESCRIPTION_KEY][configData.LABEL_KEYS.VALUE_KEY];
        }
      }
    }
    return (
      <table width="85%"
        cellSpacing="0"
      >
        <tbody>
          {description !== null?
            <>
            <tr>
              <td width="15%" valign='top' 
                  style={{backgroundColor:"var(--one-color-background-intensity-low)"}}>
                <OwcTypography variant="title6" style={{paddingLeft: "0.5em"}}>Description</OwcTypography>
              </td>
              <td style={{paddingBottom: "0.5em", paddingLeft:"0.5em",
                          border:"1px solid var(--one-color-background-intensity-low)"}}>
                <OwcTypography>{description}</OwcTypography></td>
            </tr>
            <tr style={{height:"0.5em"}}/>
            </>
          :
            ""
          }
          <tr>
            <td valign='top' style={{backgroundColor:"var(--one-color-background-intensity-low)"}}>
              <OwcTypography variant="title6" style={{marginTop: "0.1em", paddingLeft: "0.5em"}}>Country Filter</OwcTypography>
            </td>
            <td style={{border:"1px solid var(--one-color-background-intensity-low)", paddingLeft:"0.5em"}}>
              <div style={{display: "flex", flexDirection: "column"}}>
                <OwcButton onclick={() => this.setState({ countrySelectorVisible: true })}>
                  Click to select countries
                </OwcButton>
                <OwcInput
                  cols="80"
                  rows="3"
                  label={this.state.countryList.length===0?
                          "No countries selected - The report will be global"
                        :
                          `${this.state.countryList.length} ${this.state.countryList.length===1?"country":"countries"} selected`
                        }
                  value={this.state.countryList}
                  type="textarea"
                  style={{ marginTop:"0.5em" }}
                  no-clear-icon resizable="false" disabled disableValidation>
              </OwcInput>
              </div>
            </td>
          </tr>

        

        </tbody>
      </table>
    );
  }

  renderDeviceDetailFilter() {
    return (
      <div style={{
            display:"flex",
            gap:"1em",
            alignItems: "flex-start",
            flexDirection: "row", 
            border: "1px solid var(--one-color-background-intensity-low)"
          }}>
      <div  style={{height: "100%", backgroundColor: "var(--one-color-background-intensity-low)"}}>
      <OwcTypography variant="title6">Device Filters&nbsp;</OwcTypography>
      </div>
      <div style={{
            display:"flex", justifyContent:"space-between", 
            flexDirection: "column",
            alignItems: "flex-start",
            marginRight: "0.5em"
          }}>
                  <select style={{ width: "100%", position: "relative", zIndex:"2" }}
                      value={this.state.filterDeviceDetailsBy}
                      onChange={(ev) => { this.setState({ filterDeviceDetailsBy: ev.target.value }) }} >
                    {this.state.deviceDetailList.map(item => (<option key={"NavSearch" + item}>{item}</option>))}
                  </select>
                  <OwcInput
                      style={{ width: "100%" }}
                      placeholder="Enter value to filter by"
                      round={false}
                      variant="filled"
                      value={this.state.deviceDetailFilter}
                      onValueChange={(ev) => this.setState({ deviceDetailFilter: ev.detail })}>
                  </OwcInput>
      </div>
      </div>
    );
  }

  renderAgreementFilter() {
    const agreementList = this.state.agreementList;
    return (
      <OwcTable style={{ marginBottom: "0.5em"}} size="dense" height="auto" >
        <OwcTableHeader elevated sticky>
          <OwcTableHeaderCell width="90%">
            <OwcTypography variant="title6">
              {`Agreement Filter - ${agreementList.length===0?"None Selected":`${agreementList.length} Selected`}`}
            </OwcTypography>
          </OwcTableHeaderCell>
        </OwcTableHeader>
        <OwcTableBody>
          <div style={{ marginLeft: "1em", marginRight: "1em", marginBottom: "1em" }}>
           <AgreementSelection selectedList={this.state.agreementList}
              resetSearch={this.state.resetAgreementControl}
              onRemoveSelectedItem={(agreementToRemove)=>{
                const newAgreementList = agreementList.filter(agreement => agreement.customerAgreementId !== agreementToRemove.customerAgreementId);
                this.setState({agreementList: newAgreementList});
              }}
              onAddSelectedItem={
                (agreement)=>{const newAgreementList = [...this.state.agreementList];
                newAgreementList.push(agreement);
                this.setState({agreementList: newAgreementList});
              }}/>
          </div>
        </OwcTableBody>
      </OwcTable>
    );
  }

  renderSystemTypeFilter() {
    const refListData = this.state.systemTypeList;
    const selectedItems = this.state.selectedSystemTypes;
    return (
      <OwcTable style={{ marginBottom: "0.5em"}} size="dense" height="auto" >
        <OwcTableHeader elevated sticky>
          <OwcTableHeaderCell width="90%" resizable>
            <OwcTypography variant="title6">
              {`Reference System Type Filter - ${selectedItems.length===0?"None Selected":`${selectedItems.length} Selected`}`}
            </OwcTypography>
          </OwcTableHeaderCell>
        </OwcTableHeader>
        <OwcTableBody>
          <div style={{ marginLeft: "1em", marginRight: "1em", marginBottom: "1em" }}>
            <ReferenceListSelector
              size="small"
              refListData={refListData}
              selectedItems={selectedItems}
              onSelectedChange={(selectedItems)=>this.setState({selectedSystemTypes: selectedItems})} />
          </div>
        </OwcTableBody>
      </OwcTable>
    );
  }



  renderReportButtons() {
    return (
      <div style={{ display: 'flex', flexDirection: "column", gap: "1em"}}>
        <OwcButton id="submitButton" onclick={this.handleSubmit}>
          Generate Report
        </OwcButton>
        <DelayedTooltip anchor="submitButton" >
          Generate report using the current specifications
        </DelayedTooltip>
        <OwcButton id="cancelButton" style={{ width:"100%" }}
            variant="primary" className="warning-button"
            onClick={this.handleCancel}>
          Cancel Request
        </OwcButton>
        <DelayedTooltip anchor="cancelButton" >
          Cancel the current request
        </DelayedTooltip>
        <OwcButton id="clearButton" style={{ width:"100%" }}
            variant="primary" className="warning-button"
            onClick={this.handleFormClear}>
          Clear
        </OwcButton>
        <DelayedTooltip anchor="clearButton" >
          Clear current specifications
        </DelayedTooltip>
      </div>
    );
  }

  renderFilters() {
    return (
      <div style={{ marginLeft: "2em", marginRight: "2em"}}>
        <div style={{ display: 'flex', flexDirection: "row",  justifyContent: 'space-between', marginBottom: "0.5em"}}>
          {this.renderCountryFilter()}
          {this.renderReportButtons()}
        </div>
        {this.renderAgreementFilter()}
        {this.renderSystemTypeFilter()}
        
        <div style={{ display: 'flex', flexDirection: "row", justifyContent: 'space-between'}}>
          {this.renderDeviceDetailFilter()}
          <OwcInput
              cols="80"
              rows="4"
              label="Disclaimer"
              value={this.REPORT_DISCLAIMER}
              type="textarea"
              no-clear-icon resizable="false" disabled>
          </OwcInput>
        </div>
      </div>
    );
  }

  /**
  * Renders the specification section for the Data Product
  * @returns formatted JSX
  */
  render() {
    return (
      <div style={{ marginLeft: "2em", marginRight: "2em"}}>
        <OwcExpandable style={{ marginTop: "0.5em" }} roundedControl variant="transparent"
            expanded={this.state.specificationExpanded}
            onExpandedChange={(ev) => { this.setState({ specificationExpanded: ev.detail }) }}>
          <span slot="title">
            <OwcTypography variant="title6">
              Report Specification
            </OwcTypography>
          </span>
          <span slot="content">
            <div style={{ display: 'flex', flexDirection: "column" }}>
              {this.renderFilters()}
            </div>
          </span>
        </OwcExpandable>
        {this.renderCountrySelectionDialog()}
      </div>
    );
  }
}

export default CountryReportSpecification;
